import api, { handleResponse, handleError } from './api';
import { getActiveId } from './accountRegistry';

/**
 * Small per-account UI prefs that should follow the person onto a new device — the
 * first-video tips flag, the voice-note skip step, the playback speed. The server holds
 * the truth (`users/me/prefs`, an allow-list); localStorage mirrors it per account so a read
 * never waits on the network and a tap applies at once. Keys and defaults live in
 * `PREF_DEFAULTS`; a new pref is added there AND on `UserUiPrefsService.AllowedKeys`.
 */
export const PREF_DEFAULTS = {
  'videoTips.seen': false,
  'introSheet.dismissed': false,
  voiceSkipSec: 5,
  voicePlaybackRate: 1,
};

const storageKey = (key) => `pr.prefs.${getActiveId() || 'anon'}.${key}`;
const listeners = new Set();
let loaded = null;   // the active account the server copy was pulled for

export function readPref(key) {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (raw != null) return JSON.parse(raw);
  } catch { /* private mode or a stale value */ }
  return PREF_DEFAULTS[key];
}

function writeLocal(key, value) {
  try {
    if (value == null) localStorage.removeItem(storageKey(key));
    else localStorage.setItem(storageKey(key), JSON.stringify(value));
  } catch { /* private mode */ }
  listeners.forEach((fn) => fn(key, value));
}

/** Set a pref: the mirror first (so the UI moves now), then the server. */
export function setPref(key, value) {
  writeLocal(key, value);
  if (!(key in PREF_DEFAULTS)) return Promise.resolve(null);
  return api.patch('/users/me/prefs', { [key]: value }).then(handleResponse).catch(handleError);
}

/** Pull the server copy once per account and fold it into the mirror. */
export async function loadPrefs() {
  const account = getActiveId() || 'anon';
  if (loaded === account) return;
  loaded = account;
  try {
    const r = handleResponse(await api.get('/users/me/prefs'));
    if (r?.success && r.data && typeof r.data === 'object') {
      Object.entries(r.data).forEach(([k, v]) => { if (k in PREF_DEFAULTS) writeLocal(k, v); });
    }
  } catch { loaded = null; }
}

/** `fn(key, value)` on every write; returns the unsubscribe. */
export function onPrefChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only. */
export function resetPrefsForTests() { loaded = null; listeners.clear(); }
